import {
  autoUpdate,
  FloatingPortal,
  flip,
  offset,
  shift,
  size,
  useDismiss,
  useFloating,
  useInteractions,
  useRole
} from '@floating-ui/react'
import {
  faCheck,
  faChevronDown,
  faXmark
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import clsx from 'clsx'
import { type ReactElement, useRef, useState } from 'react'
import { useFloatingTransition } from '~/hooks/useFloatingTransition'
import { themeClass } from '~/styles/theme.css'
import type { DropdownOption, SelectedSubOptions, SubOption } from './Dropdown'
import {
  multiSelectContainer,
  optionsPortal,
  optionsWrapper,
  removeTagIcon,
  selectedTag,
  selectedValue,
  selectedValueInner,
  selectedValueText,
  selectedValueTextParent,
  subOptionItem,
  subOptionsContainer,
  subOptionsToggle
} from './Dropdown.css'

type DropdownOptionWithParent = DropdownOption & {
  parent?: DropdownOption
}

type SubOptionDropdownProps = {
  availableSubOptions: SubOption[]
  currentSubOptions: string[]
  onToggleSubOption: (subOptionValue: string) => void
  isUpdating: boolean
  disabled: boolean | string
  children: ReactElement
}

const SubOptionDropdown = ({
  availableSubOptions,
  currentSubOptions,
  onToggleSubOption,
  isUpdating,
  disabled,
  children
}: SubOptionDropdownProps): ReactElement => {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const { refs, floatingStyles, context } = useFloating({
    placement: 'bottom-start',
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [
      offset(5),
      flip(),
      shift(),
      size({
        apply({ availableHeight, elements }) {
          Object.assign(elements.floating.style, {
            maxHeight: `${Math.min(200, Math.max(0, availableHeight))}px`
          })
        }
      })
    ],
    whileElementsMounted: autoUpdate
  })

  const { isMounted, styles: transitionStyles } = useFloatingTransition(context)

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useDismiss(context, {
      outsidePress: () => true,
      escapeKey: true
    }),
    useRole(context, { role: 'listbox' })
  ])

  return (
    <>
      <div
        ref={refs.setReference}
        {...getReferenceProps()}
        onClick={(e) => {
          e.stopPropagation() // Prevent main dropdown from opening
          setIsOpen(!isOpen) // Manual toggle
        }}
      >
        {children}
      </div>
      <FloatingPortal>
        {isMounted && (
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            className={optionsPortal}
            {...getFloatingProps()}
          >
            <div
              ref={dropdownRef}
              style={{ ...transitionStyles }}
              className={clsx(themeClass, optionsWrapper)}
            >
              {availableSubOptions.map((subOption) => (
                <div
                  key={subOption.value}
                  className={subOptionItem}
                  onClick={(e) => {
                    if (isUpdating || disabled !== false) {
                      return
                    }
                    e.stopPropagation()
                    onToggleSubOption(subOption.value)
                  }}
                >
                  {subOption.label}
                  {currentSubOptions.includes(subOption.value) && (
                    <FontAwesomeIcon
                      icon={faCheck}
                      style={{
                        marginLeft: 'auto',
                        fontSize: '0.8em'
                      }}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </FloatingPortal>
    </>
  )
}

type SelectedValuesProps = {
  selectedValues: string[]
  optionsSelectedStore: DropdownOptionWithParent[]
  multiple: boolean
  required: boolean
  emptyLabel?: string
  placeholder?: string | ReactElement
  isUpdating: boolean
  disabled: boolean | string
  selectedSubOptions: SelectedSubOptions
  globalSubOptions?: SubOption[]
  subOptions?: { [key: string]: SubOption[] }
  allowMultipleSubOptions: boolean
  onValueBoxClick?: () => void
  onRemoveValue: (value: string) => void
  onToggleSubOption: (primaryValue: string, subOptionValue: string) => void
  buildParentLabelPath: (option: DropdownOptionWithParent) => string
  getSubOptionsForValue: (value: string) => SubOption[]
  setIsOpen: (open: boolean) => void
}

export const DropdownSelectedValues = ({
  selectedValues,
  optionsSelectedStore,
  multiple,
  required,
  emptyLabel,
  placeholder,
  isUpdating,
  disabled,
  selectedSubOptions,
  onValueBoxClick,
  onRemoveValue,
  onToggleSubOption,
  buildParentLabelPath,
  getSubOptionsForValue,
  setIsOpen
}: SelectedValuesProps): ReactElement => {
  return (
    <div className={multiSelectContainer} onClick={onValueBoxClick}>
      {selectedValues.length === 0 ? (
        <div className={selectedValueText}>{placeholder}</div>
      ) : (
        selectedValues.map((value) => {
          const selectedOption = optionsSelectedStore.find(
            (opt) => opt.value === value
          )
          const canRemove =
            (multiple || !required) &&
            (emptyLabel === undefined || value !== '')
          const availableSubOptions = getSubOptionsForValue(value)
          const hasSubOptions = availableSubOptions.length > 0
          const currentSubOptions = selectedSubOptions[value] || []

          return (
            <div
              key={value}
              className={multiple ? selectedTag : selectedValue}
              onClick={(e) => {
                if (isUpdating || disabled !== false) {
                  e.stopPropagation()
                  return
                }
                setIsOpen(false)
              }}
            >
              <div className={selectedValueInner}>
                {selectedOption?.element}
                {!selectedOption?.element && (
                  <span className={selectedValueInner}>
                    {selectedOption?.parent && (
                      <span className={selectedValueTextParent}>
                        {buildParentLabelPath(selectedOption.parent)}
                      </span>
                    )}
                    <span className={selectedValueText}>
                      {selectedOption?.label}
                    </span>
                  </span>
                )}
                {hasSubOptions && currentSubOptions.length > 0 && (
                  <div className={subOptionsContainer}>
                    {currentSubOptions
                      .map(
                        (subValue) =>
                          availableSubOptions.find(
                            (so) => so.value === subValue
                          )?.label
                      )
                      .join(', ')}
                  </div>
                )}
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4
                }}
              >
                {hasSubOptions && (
                  <SubOptionDropdown
                    availableSubOptions={availableSubOptions}
                    currentSubOptions={currentSubOptions}
                    onToggleSubOption={(subOptionValue) =>
                      onToggleSubOption(value, subOptionValue)
                    }
                    isUpdating={isUpdating}
                    disabled={disabled}
                  >
                    <FontAwesomeIcon
                      icon={faChevronDown}
                      className={clsx(subOptionsToggle)}
                    />
                  </SubOptionDropdown>
                )}
                {canRemove && (
                  <FontAwesomeIcon
                    icon={faXmark}
                    className={removeTagIcon}
                    onClick={(e) => {
                      if (isUpdating || disabled !== false) {
                        return
                      }
                      e.stopPropagation()
                      onRemoveValue(value)
                    }}
                  />
                )}
              </div>
            </div>
          )
        })
      )}
    </div>
  )
}
